import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import {
  FirestoreOrganisationLanguage,
  FirestoreOrganisationMarketplace,
  FirestoreOrganisationSettings,
} from "reedy-data/api";
import { useEffect, useState } from "react";
import { ActionCard } from "~/components/card/action-card";
import { Text } from "~/components/text";
import { useOrganisation } from "~/contexts/organisation-provider";
import { toast } from "~/utils/toast";
import { LanguageSettingsMarketplaceDropdown } from "./components/language-settings-marketplace-dropdown";
import { LanguageSettingsLanguageDropdown } from "./components/language-settings-language-dropdown";

export const OrganisationSettingsPage = () => {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const {
    id: organisationId,
    organisation,
    updateOrganisationSettings,
    loading: organisationLoading,
  } = useOrganisation();
  const [loading, setLoading] = useState<
    Partial<{ [K in keyof FirestoreOrganisationSettings]: boolean }>
  >({});
  const [settings, setSettings] = useState<FirestoreOrganisationSettings>(
    organisation?.settings || {},
  );

  useEffect(() => {
    // keeps the editable language settings in sync with the organisation document loaded from the backend; the same state is optimistically edited on save, so it cannot be derived
    setSettings(organisation?.settings || {});
  }, [organisation]);

  const handleSettingChange = async (values: FirestoreOrganisationSettings) => {
    try {
      setLoading({
        language: Boolean(values.language),
        marketplace: Boolean(values.marketplace),
      });
      setSettings((prev) => ({ ...prev, ...values }));
      await updateOrganisationSettings({
        language: values.language || undefined,
        marketplace: values.marketplace || undefined,
      });
      toast.success(t("organisation.settings.languageSettings.saved"));
    } catch (e) {
      setSettings(organisation?.settings || {});
      toast.error(
        t("organisation.settings.languageSettings.error", {
          message: (e as Error).message,
        }),
      );
    } finally {
      setLoading({});
    }
  };

  return (
    <div className="space-y-10">
      <section>
        <Text as="h2" variant="pageHeading" className="mb-2">
          {t("organisation.settings.globalFilters.title")}
        </Text>
        <Text variant="subtle" className="pb-4">
          {t("organisation.settings.globalFilters.description")}
        </Text>

        <div className="space-y-4">
          <ActionCard
            title={t("organisation.settings.productAvailability.title")}
            iconName="truck"
            onClick={() =>
              navigate({
                to: "/settings/organisations/$organisationId/settings/product-availability",
                params: { organisationId },
              })
            }
          >
            <ActionCard.Action
              iconName="chevron-right"
              label={t("organisation.productAvailability.actions.navigate")}
              testId="product-availability-action"
            />
          </ActionCard>
        </div>
      </section>
      <section>
        <Text as="h2" variant="pageHeading" className="mb-2">
          {t("organisation.settings.languageSettings.title")}
        </Text>
        <Text variant="subtle" className="pb-4">
          {t("organisation.settings.languageSettings.description")}
        </Text>
        <div>
          <ActionCard
            title={t("organisation.settings.marketplace.title")}
            subtitle={t("organisation.settings.marketplace.subtitle")}
            iconName="earth"
          >
            <LanguageSettingsMarketplaceDropdown
              className="w-full"
              disabled={
                !organisation || loading.marketplace || organisationLoading
              }
              value={
                settings?.marketplace || FirestoreOrganisationMarketplace.NONE
              }
              onChange={(marketplace) => handleSettingChange({ marketplace })}
            />
          </ActionCard>
          <ActionCard
            title={t("organisation.settings.language.title")}
            subtitle={t("organisation.settings.language.subtitle")}
            iconName="message-circle"
          >
            <LanguageSettingsLanguageDropdown
              className="w-full"
              disabled={
                !organisation || loading.language || organisationLoading
              }
              value={settings?.language || FirestoreOrganisationLanguage.EN_US}
              onChange={(language) => handleSettingChange({ language })}
            />
          </ActionCard>
        </div>
      </section>
    </div>
  );
};
